import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import NavBar, { BrandMark } from "./components/layouts/Navbar";
import "./App.css";

import { useDispatch, useSelector } from "react-redux";

import { onAuthStateChanged, signOut } from "./backend/client";
import { auth } from "./firebaseConfig";
import { setUser, logout } from "./context/actions/authActions";
import ProtectedRoute from "./components/ProtectedRoute";

const CreateEmployeePage = lazy(() => import("./components/page/form/CreateEmployeePage"));
const Home = lazy(() => import("./components/page/home"));
const Login = lazy(() => import("./components/page/login"));
const ItemsReport = lazy(() => import("./components/page/reports/itemsReport"));
const Chat = lazy(() => import("./components/page/reports/chat"));
const Reports = lazy(() => import("./components/page/reports"));
const EditClientPage = lazy(() => import("./components/page/reports/EditClientPage"));
const AddAdvertisement = lazy(() => import("./components/page/form/addAdvertisement"));
const CurrenciesForm = lazy(() => import("./components/page/form/CurrenciesForm"));
const PaymentMethodForm = lazy(() => import("./components/page/form/PaymentMethodForm"));
const Balance = lazy(() => import("./components/page/reports/balance"));
const ExternalTransfer = lazy(() => import("./components/page/form/externalTransfer"));
const AdminVerification = lazy(() => import("./components/page/form/adminVerification"));
const AddNotification = lazy(() => import("./components/page/form/AddNotification"));
const CashRequestsReport = lazy(() => import("./components/page/reports/cashRequestsReport"));
const RegionsForm = lazy(() => import("./components/page/form/RegionsForm"));
const AppUpdatePolicy = lazy(() => import("./components/page/form/AppUpdatePolicy"));
const TransferFeesForm = lazy(() => import("./components/page/form/TransferFeesForm"));

const SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 ساعات بالميلي ثانية
const THEME_STORAGE_KEY = "wasel.admin.theme";

function getInitialTheme() {
  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  return savedTheme === "dark" ? "dark" : "light";
}

export default function App() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.documentElement.style.colorScheme = theme;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const tokenResult = await user.getIdTokenResult();
          if (tokenResult.claims.admin !== true) {
            await signOut(auth);
            dispatch(logout());
            localStorage.removeItem("token");
            localStorage.removeItem("tokenExpiration");
            localStorage.removeItem("loginAt");
            return;
          }
          const expirationTime = Date.now() + SESSION_DURATION;

          localStorage.setItem("token", tokenResult.token);
          localStorage.setItem("tokenExpiration", String(expirationTime));
          if (!localStorage.getItem("loginAt")) {
            localStorage.setItem("loginAt", String(Date.now()));
          }
          dispatch(setUser(user));
        } else {
          dispatch(logout());
          localStorage.removeItem("token");
          localStorage.removeItem("tokenExpiration");
          localStorage.removeItem("loginAt");
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        dispatch(logout());
        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpiration");
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  // تسجيل الخروج التلقائي بعد 8 ساعات من تسجيل الدخول
  useEffect(() => {
    if (!isLoggedIn) return;

    const loginAt = Number(localStorage.getItem("loginAt") || 0);
    if (!loginAt) return;

    const remaining = SESSION_DURATION - (Date.now() - loginAt);

    if (remaining <= 0) {
      signOut(auth);
      dispatch(logout());
      return;
    }

    const timer = setTimeout(async () => {
      await signOut(auth);
      dispatch(logout());
      localStorage.removeItem("loginAt");
    }, remaining);

    return () => clearTimeout(timer);
  }, [isLoggedIn, dispatch]);

  if (isLoading) {
    return (
      <div className="wasel-admin-app" data-theme={theme} dir="rtl">
        <div className="app-loading" role="status" aria-live="polite">
          <BrandMark />
          <span className="spinner" aria-hidden="true" />
          <p>جاري تجهيز لوحة التحكم...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`wasel-admin-app ${isLoggedIn ? "admin-shell" : "auth-shell"}`}
      data-theme={theme}
      dir="rtl"
    >
      <BrowserRouter>
        {isLoggedIn && (
          <NavBar
            theme={theme}
            onToggleTheme={() =>
              setTheme((currentTheme) =>
                currentTheme === "light" ? "dark" : "light"
              )
            }
          />
        )}

        <main className={isLoggedIn ? "admin-content" : "auth-content"}>
          <Suspense fallback={<div className="app-loading" role="status"><span className="spinner" /><p>جاري تحميل الصفحة...</p></div>}>
          <Routes>
            <Route
              path="/login"
              element={isLoggedIn ? <Navigate to="/" /> : <Login />}
            />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />

            <Route
              path="/regions"
              element={
                <ProtectedRoute>
                  <RegionsForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/balance"
              element={
                <ProtectedRoute>
                  <Balance />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cash_requests"
              element={
                <ProtectedRoute>
                  <CashRequestsReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notification"
              element={
                <ProtectedRoute>
                  <AddNotification />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              }
            />

            <Route path="/customers/new" element={<ProtectedRoute><CreateEmployeePage /></ProtectedRoute>} />

            <Route
              path="/edit-client/:id"
              element={
                <ProtectedRoute>
                  <EditClientPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/CurrenciesForm"
              element={
                <ProtectedRoute>
                  <CurrenciesForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/PaymentMethodForm"
              element={
                <ProtectedRoute>
                  <PaymentMethodForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/external_transfer"
              element={
                <ProtectedRoute>
                  <ExternalTransfer />
                </ProtectedRoute>
              }
            />

            <Route
              path="/transfer_fees"
              element={
                <ProtectedRoute>
                  <TransferFeesForm />
                </ProtectedRoute>
              }
            />

            <Route
              path="/add_advertisement"
              element={
                <ProtectedRoute>
                  <AddAdvertisement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/item_reports"
              element={
                <ProtectedRoute>
                  <ItemsReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin_verification"
              element={
                <ProtectedRoute>
                  <AdminVerification />
                </ProtectedRoute>
              }
            />

            <Route
              path="/chat/:orderId"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />

            <Route
              path="/app_updates"
              element={
                <ProtectedRoute>
                  <AppUpdatePolicy />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to={isLoggedIn ? "/" : "/login"} replace />} />
          </Routes>
          </Suspense>
        </main>
      </BrowserRouter>
    </div>
  );
}
